import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, CalendarDays, Clock, XCircle, CheckCircle2, AlertCircle,
  Upload, Mic, Trash2,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const LEAVE_TYPES = [
  { value: "PL", icon: "💼", label: "Paid Leave (PL)",
    rule: "Apply before or after — flexible.",
    best: "Planned vacation, family events", deducts: true },
  { value: "EL", icon: "🌟", label: "Earned Leave (EL)",
    rule: "Must apply ≥24 hours in advance.",
    best: "Planned long leaves, year-end encashable", deducts: true },
  // CL bucket removed — Bravura uses only EL/PL/SL + Short/WFH/OD
  { value: "SL", icon: "🏥", label: "Sick Leave (SL)",
    rule: "Apply within 48 hours. Medical cert for 3+ days.",
    best: "Illness, recovery", deducts: true },
  { value: "SHORT_EARLY", icon: "🕐", label: "Short Leave — Early Out",
    rule: "Max 2/month. Office staff only. ≤60 min.",
    best: "Leave early for appointment", deducts: false,
    whiteCollarOnly: true },
  { value: "SHORT_LATE", icon: "🕘", label: "Short Leave — Late Arrival",
    rule: "Max 2/month. Office staff only. ≤60 min.",
    best: "Arrive late after errand", deducts: false,
    whiteCollarOnly: true },
  { value: "WFH", icon: "🏠", label: "Work From Home (WFH)",
    rule: "Apply ≥24 hours in advance.",
    best: "Office staff, sick recovery", deducts: false },
  { value: "OD", icon: "📍", label: "On Duty (OD)",
    rule: "Customer/location required. Counts as present.",
    best: "Field/client visit, sales", deducts: false },
];

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-50 text-slate-600 border-slate-200",
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function diffDays(from, to, half) {
  if (!from || !to) return 0;
  const f = new Date(from), t = new Date(to);
  if (f > t) return 0;
  const n = Math.round((t - f) / 86400000) + 1;
  if (half && n === 1) return 0.5;
  return n;
}

export default function PortalLeaves() {
  const [rows, setRows] = useState([]);
  const [bal, setBal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    leave_type: "PL",
    from_date: todayISO(),
    to_date: todayISO(),
    half_day: false,
    half_day_session: "morning",
    reason: "",
    short_leave_time: "17:00",
    short_leave_minutes: 60,
    customer_location: "",
    is_emergency: false,
    medical_certificate_url: "",
    voice_note_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState(null); // {ok, msg, type:'success'|'error'}
  const [uploadingCert, setUploadingCert] = useState(false);
  const [voiceExpanded, setVoiceExpanded] = useState(false);
  const [eligibility, setEligibility] = useState(null);  // Phase 5D: per-employee toggles
  const [autoSplit, setAutoSplit] = useState(null);      // Phase 5D: live EL/PL/LWP breakdown

  // Phase 5D — only show leave types that are enabled for this employee
  const visibleLeaveTypes = useMemo(() => {
    if (!eligibility) return LEAVE_TYPES;
    return LEAVE_TYPES.filter((t) => {
      switch (t.value) {
        case "EL": return eligibility.el_active;
        case "PL": return eligibility.pl_active;
        case "SHORT_EARLY": return eligibility.short_early_applicable;
        case "SHORT_LATE": return eligibility.short_late_applicable;
        case "WFH": return eligibility.wfh_applicable;
        case "OD": return eligibility.od_applicable;
        default: return true; // CL, SL always visible
      }
    });
  }, [eligibility]);

  const refresh = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: list }, { data: dash }, eligibilityRes] = await Promise.all([
        api.get("/my-portal/leaves"),
        api.get("/my-portal/dashboard"),
        api.get("/my-portal/leave-eligibility").catch(() => ({ data: null })),
      ]);
      setRows(list || []);
      setBal(dash?.leave_balance || null);
      if (eligibilityRes.data) setEligibility(eligibilityRes.data);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Auto-refresh every 60s + on tab focus (catches HR overrides applied while user is on this page)
    const intervalId = setInterval(() => refresh(true), 60_000);
    const onFocus = () => refresh(true);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 5D — if current selection isn't visible anymore, switch to first visible
  useEffect(() => {
    if (!eligibility) return;
    const stillVisible = visibleLeaveTypes.some((t) => t.value === form.leave_type);
    if (!stillVisible && visibleLeaveTypes.length > 0) {
      setForm((f) => ({ ...f, leave_type: visibleLeaveTypes[0].value }));
    }
  }, [eligibility, visibleLeaveTypes, form.leave_type]);

  // Phase 5D — fetch auto-split preview when EL/PL selected
  useEffect(() => {
    if (!open) return;
    if (form.leave_type !== "EL" && form.leave_type !== "PL") {
      setAutoSplit(null);
      return;
    }
    if (!form.from_date || !form.to_date) return;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/my-portal/leaves/auto-split-preview", {
          from_date: form.from_date,
          to_date: form.to_date,
          half_day: form.half_day,
        }, { signal: ctrl.signal });
        setAutoSplit(data);
      } catch (e) {
        if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
        // Silently ignore preview failures
      }
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [open, form.leave_type, form.from_date, form.to_date, form.half_day]);

  const days = useMemo(
    () => diffDays(form.from_date, form.to_date, form.half_day),
    [form.from_date, form.to_date, form.half_day]
  );

  // Live policy validation (debounced via leave_type + dates change)
  useEffect(() => {
    if (!open) return;
    if (!form.from_date || !form.to_date) return;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/my-portal/leaves/validate", form, { signal: ctrl.signal });
        setValidation({ ok: true, msg: `Valid — ${data.days} day(s)`, type: "success" });
      } catch (e) {
        if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
        setValidation({ ok: false, msg: formatApiErrorDetail(e.response?.data?.detail), type: "error" });
      }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [open, form.leave_type, form.from_date, form.to_date, form.half_day,
      form.short_leave_minutes, form.customer_location, form.medical_certificate_url]);

  const selectedRule = useMemo(
    () => LEAVE_TYPES.find((t) => t.value === form.leave_type),
    [form.leave_type]
  );

  const onApply = async () => {
    if (days <= 0) {
      toast.error("Please pick valid From/To dates");
      return;
    }
    setSubmitting(true);
    try {
      // Phase 5D — EL/PL go through auto-split endpoint (server decides EL/PL/LWP mix)
      if (form.leave_type === "EL" || form.leave_type === "PL") {
        await api.post("/my-portal/leaves/apply-auto", {
          from_date: form.from_date,
          to_date: form.to_date,
          half_day: form.half_day,
          half_day_session: form.half_day_session,
          reason: form.reason,
        });
        const lwpPart = autoSplit?.breakdown?.find((b) => b.type === "LWP");
        if (lwpPart) {
          toast.success(`Submitted — ${lwpPart.days} day(s) will be unpaid (LWP)`);
        } else {
          toast.success("Leave request submitted. Email sent to your HOD.");
        }
      } else {
        await api.post("/my-portal/leaves/apply", form);
        toast.success("Leave request submitted. Email sent to your HOD.");
      }
      setOpen(false);
      setForm({ ...form, reason: "", customer_location: "",
                medical_certificate_url: "", voice_note_url: "" });
      setVoiceExpanded(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try {
      await api.delete(`/my-portal/leaves/${id}`);
      toast.success("Request cancelled");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---- Medical certificate upload ----
  const onUploadCert = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File exceeds 5MB limit");
      return;
    }
    setUploadingCert(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "leave_medical");
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `/api/files/${data.path}`;
      setForm((f) => ({ ...f, medical_certificate_url: url }));
      toast.success("Medical certificate uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingCert(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-leaves">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Leaves</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Apply &amp; Track Leaves
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            8 leave types · time-based rules · email + magic-link approval (Manager + HOD + Senior Mgmt + HR in CC).
          </p>
        </div>
        <Button
          onClick={() => { setOpen(true); setValidation(null); }}
          className="brand-bg brand-bg-hover text-white"
          data-testid="apply-leave-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Apply for Leave
        </Button>
      </div>

      {/* Balance cards — only show buckets the employee is enabled for
           (CL bucket removed — Bravura uses only EL/PL). SL shown only if ledger has data. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="leave-balances">
        {["EL", "PL", "SL"].filter((t) => {
          // EL/PL hidden when toggle is off (matches apply-form visibility)
          if (t === "EL") return eligibility?.el_applicable ?? true;
          if (t === "PL") return eligibility?.pl_applicable ?? true;
          // SL has no per-employee toggle, always shown
          return true;
        }).map((t) => {
          const raw = Number(bal?.[t] ?? 0);
          const overUsed = raw < 0;
          const display = overUsed ? 0 : raw;
          return (
            <div key={t} className="bg-white rounded-xl border border-red-100 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{t} Balance</div>
                {overUsed && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-red-700 bg-red-100 rounded px-1.5 py-0.5"
                        data-testid={`over-used-${t}`}>
                    Over-used
                  </span>
                )}
              </div>
              <div className={`text-2xl font-bold mt-1 ${overUsed ? "text-red-700" : "brand-text"}`}
                   style={{ fontFamily: "Chivo" }}>
                {display}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Used: {bal?.[`${t}_used_ytd`] ?? 0}
                {overUsed && (
                  <span className="ml-1 text-red-700 font-medium">
                    ({Math.abs(raw)} {Math.abs(raw) === 1 ? "day" : "days"} over)
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* History */}
      <section className="bg-white rounded-xl border border-red-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-red-100">
          <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <CalendarDays className="h-4 w-4 brand-text" /> My Leave History
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            <AlertCircle className="h-5 w-5 inline-block mr-2 text-slate-400" />
            No leave requests yet. Click "Apply for Leave" to submit one.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="leaves-table">
            <thead className="bg-red-50/50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Applied</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">From → To</th>
                <th className="text-right px-4 py-2">Days</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-red-50" data-testid={`leave-row-${r.id}`}>
                  <td className="px-4 py-2 text-slate-500 text-xs">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-2 font-semibold text-slate-900">{r.leave_type}</td>
                  <td className="px-4 py-2">
                    {formatDate(r.from_date)}{r.from_date === r.to_date ? "" : ` → ${formatDate(r.to_date)}`}
                  </td>
                  <td className="px-4 py-2 text-right">{r.days}{r.half_day ? " (½)" : ""}</td>
                  <td className="px-4 py-2 text-slate-600 max-w-[280px] whitespace-normal break-words align-top" title={r.reason}>
                    {r.reason || "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium border px-2 py-0.5 rounded ${STATUS_BADGE[r.status] || ""}`}>
                      {r.status === "approved" && <CheckCircle2 className="h-3 w-3" />}
                      {r.status === "rejected" && <XCircle className="h-3 w-3" />}
                      {r.status === "pending" && <Clock className="h-3 w-3" />}
                      {r.status}
                    </span>
                    {r.is_lwp && r.status === "approved" && (
                      <div className="text-[10px] text-amber-700 mt-1">⚠ LWP</div>
                    )}
                    {r.decision_remarks && (
                      <div className="text-[10px] text-slate-500 mt-0.5 italic max-w-[220px] whitespace-normal break-words" title={r.decision_remarks}>
                        {r.decision_remarks}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {r.status === "pending" && (
                      <button
                        onClick={() => cancel(r.id)}
                        className="text-xs font-medium text-red-700 hover:underline"
                        data-testid={`leave-cancel-${r.id}`}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="apply-leave-dialog">
          <DialogHeader>
            <DialogTitle>Apply for Leave</DialogTitle>
            <DialogDescription>
              Your Manager will be emailed for approval. HOD, Senior Mgmt & HR are CC&apos;d.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Leave-type tile selector */}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Leave Type</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid="leave-type-tiles">
                {visibleLeaveTypes.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setForm({ ...form, leave_type: t.value })}
                    className={`text-left rounded-lg border p-2.5 transition-all hover:shadow-sm ${
                      form.leave_type === t.value
                        ? "border-red-400 ring-2 ring-red-200 bg-red-50/50"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`leave-tile-${t.value}`}
                  >
                    <div className="flex items-start gap-1.5">
                      <span className="text-base">{t.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-[12px] leading-tight">{t.label}</div>
                        {t.deducts && (
                          <div className="text-[10px] brand-text mt-0.5 font-medium">
                            {Math.max(0, Number(bal?.[t.value] ?? 0))} <span className="text-slate-400 font-normal">days</span>
                          </div>
                        )}
                        <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{t.rule}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {selectedRule?.whiteCollarOnly && (
                <div className="text-[11px] text-amber-700 mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Available only for office / white-collar staff.
                </div>
              )}
            </div>

            {/* Date pickers */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">From</Label>
                <Input
                  type="date"
                  value={form.from_date}
                  onChange={(e) => setForm({ ...form, from_date: e.target.value, to_date: e.target.value > form.to_date ? e.target.value : form.to_date })}
                  className="mt-1.5"
                  data-testid="leave-from-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">To</Label>
                <Input
                  type="date"
                  value={form.to_date}
                  onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                  min={form.from_date}
                  className="mt-1.5"
                  data-testid="leave-to-input"
                />
              </div>
            </div>

            {/* Half-day (only for ledger-tracked types) */}
            {selectedRule?.deducts && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.half_day}
                  onChange={(e) => setForm({ ...form, half_day: e.target.checked, to_date: form.from_date })}
                  disabled={form.from_date !== form.to_date}
                  data-testid="leave-half-checkbox"
                />
                Half day (single-day only)
                {form.half_day && (
                  <Select
                    value={form.half_day_session}
                    onValueChange={(v) => setForm({ ...form, half_day_session: v })}
                  >
                    <SelectTrigger className="w-[140px] h-8 text-xs ml-2" data-testid="half-session-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning</SelectItem>
                      <SelectItem value="afternoon">Afternoon</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </label>
            )}

            {/* Short-leave info (time picker removed — duration fixed at 60 min) */}
            {(form.leave_type === "SHORT_EARLY" || form.leave_type === "SHORT_LATE") && (
              <div className="bg-amber-50/40 border border-amber-100 rounded p-3 text-xs text-amber-900">
                <div className="font-semibold mb-1">Short Leave — 1 hour</div>
                <ul className="list-disc list-inside space-y-0.5 text-amber-800">
                  <li>{form.leave_type === "SHORT_EARLY" ? "Early Out" : "Late Arrival"} — duration auto-set to 60 min.</li>
                  <li>Combined cap: <b>2 per month</b> (Early + Late together).</li>
                  <li>This won&apos;t count as a "Late" punch for salary calculation.</li>
                </ul>
              </div>
            )}

            {/* OD customer/location */}
            {form.leave_type === "OD" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                  Customer / Location <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.customer_location}
                  onChange={(e) => setForm({ ...form, customer_location: e.target.value })}
                  placeholder="e.g. ABC Corp Pvt Ltd, Sector 18, Noida"
                  className="mt-1.5"
                  data-testid="customer-location-input"
                />
              </div>
            )}

            {/* SL medical cert (3+ days) — file upload */}
            {form.leave_type === "SL" && days >= 3 && (
              <div className="bg-red-50/40 border border-red-100 rounded p-3 space-y-2">
                <Label className="text-[11px] uppercase tracking-wider text-red-700">
                  Medical Certificate <span className="text-red-500">*</span>
                </Label>
                {form.medical_certificate_url ? (
                  <div className="flex items-center justify-between gap-2 bg-white border border-red-200 rounded px-3 py-2 text-xs">
                    <a href={form.medical_certificate_url} target="_blank" rel="noreferrer"
                       className="brand-text underline truncate" data-testid="medical-cert-preview">
                      📎 Certificate attached — click to preview
                    </a>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, medical_certificate_url: "" })}
                      className="text-red-700 hover:underline text-xs flex items-center gap-0.5"
                      data-testid="medical-cert-remove"
                    >
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-red-200 rounded p-3 text-xs text-red-700 cursor-pointer hover:bg-red-50">
                    <Upload className="h-4 w-4" />
                    <span>{uploadingCert ? "Uploading..." : "Upload certificate (PDF / JPG / PNG, max 5MB)"}</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => onUploadCert(e.target.files?.[0])}
                      className="hidden"
                      disabled={uploadingCert}
                      data-testid="medical-cert-input"
                    />
                  </label>
                )}
                <div className="text-[10px] text-red-700">
                  Required for sick leave of 3 or more days.
                </div>
              </div>
            )}

            <div className="text-xs text-slate-500">
              <b>{days || 0}</b> day(s) requested
              {selectedRule?.deducts && bal?.[form.leave_type] != null && (
                <span className="ml-2">
                  · Balance after: <b>{Math.max(0, Math.max(0, Number(bal[form.leave_type] || 0)) - (days || 0))}</b>
                </span>
              )}
            </div>

            {/* Phase 5D — Auto-Split preview for EL/PL */}
            {(form.leave_type === "EL" || form.leave_type === "PL") && autoSplit && (
              <div className="bg-red-50/60 border border-red-200 rounded p-3 text-xs space-y-1.5" data-testid="auto-split-preview">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-red-900">Auto-Split Preview</div>
                  <div className="text-[10px] text-slate-500">
                    Balance · EL <b>{autoSplit.balances.el}</b> / PL <b>{autoSplit.balances.pl}</b>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {autoSplit.breakdown.map((b, idx) => {
                    const cls = b.type === "LWP"
                      ? "bg-red-100 text-red-800 border-red-200"
                      : "bg-emerald-100 text-emerald-800 border-emerald-200";
                    return (
                      <span key={idx} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${cls} text-[11px] font-medium`}>
                        {b.type}: {b.days} day{b.days !== 1 ? "s" : ""}
                      </span>
                    );
                  })}
                </div>
                {autoSplit.warnings.map((w, i) => (
                  <div key={i} className="text-[11px] text-amber-700 flex items-start gap-1">
                    <span>⚠</span><span>{w}</span>
                  </div>
                ))}
                <div className="text-[10px] text-slate-500 italic pt-1 border-t border-red-100">
                  EL/PL is consumed first based on available balance. Anything beyond is LWP (salary deduction).
                </div>
              </div>
            )}

            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason</Label>
              <Textarea
                rows={3}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Be specific. E.g. 'Sister's wedding on 15 May. Travel + ceremony 2 days.'"
                className="mt-1.5"
                data-testid="leave-reason-input"
              />
            </div>

            {/* Voice note recorder (collapsed by default) */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="voice-note-section">
              {!voiceExpanded && !form.voice_note_url ? (
                <button
                  type="button"
                  onClick={() => setVoiceExpanded(true)}
                  className="w-full flex items-center justify-between gap-2 text-left text-xs text-slate-600 hover:text-slate-900"
                  data-testid="voice-note-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Mic className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-medium">Add a voice note (optional · max 60s)</span>
                  </span>
                  <span className="text-[10px] text-slate-400">▼</span>
                </button>
              ) : (
                <>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                      Voice Note <span className="text-slate-400">(max 60s)</span>
                    </Label>
                    <div className="text-[10px] text-slate-500 mt-0.5 mb-2">
                      Record reason in Hindi/English — useful for emergencies.
                    </div>
                  </div>
                  <VoiceRecorder
                    value={form.voice_note_url || null}
                    onChange={(url) => setForm((f) => ({ ...f, voice_note_url: url || "" }))}
                    uploadEndpoint="/upload"
                    uploadFields={{ purpose: "leave_voice" }}
                    urlFromResponse={(d) => `/api/files/${d.path}`}
                    testIdPrefix="voice"
                    compact
                  />
                  {!form.voice_note_url && (
                    <button
                      type="button"
                      onClick={() => setVoiceExpanded(false)}
                      className="text-[10px] text-slate-400 hover:text-slate-600"
                    >
                      collapse
                    </button>
                  )}
                </>
              )}
            </div>

            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.is_emergency}
                onChange={(e) => setForm({ ...form, is_emergency: e.target.checked })}
                data-testid="emergency-checkbox"
              />
              <span>Emergency leave (will require justification)</span>
            </label>

            {/* Live validation banner */}
            {validation && (
              <div
                className={`text-xs rounded p-2 border flex items-start gap-2 ${
                  validation.type === "success"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
                data-testid="validation-banner"
              >
                {validation.type === "success"
                  ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  : <XCircle className="h-4 w-4 flex-shrink-0" />}
                <span className="whitespace-pre-line">{validation.msg}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={onApply}
              disabled={submitting || days <= 0 || (validation && validation.type === "error")}
              className="brand-bg brand-bg-hover text-white"
              data-testid="leave-submit-btn"
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
